import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, API } from "@/lib/api";
import { FileText, Download, Folder, File, AlertCircle, Loader2, LogIn } from "lucide-react";

export default function SharedDocuments() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [needsLogin, setNeedsLogin] = useState(false);
  const [loading, setLoading] = useState(true);
  // Who the link belongs to — fetched without auth so the page wears the COMMUNITY (its logo,
  // colour and concierge name) even on the sign-in screen. A resident following a texted
  // document link should never land on a page branded with the platform's name.
  const [brand, setBrand] = useState(null);

  useEffect(() => {
    api.get(`/share/${token}/brand`).then((r) => setBrand(r.data)).catch(() => {});
  }, [token]);

  useEffect(() => {
    api.get(`/share/${token}`)
      .then((r) => setData(r.data))
      .catch((e) => {
        const status = e.response?.status;
        if (status === 401) setNeedsLogin(true);
        else setError(e.response?.data?.detail || "This link is no longer available");
      })
      .finally(() => setLoading(false));
  }, [token]);

  const download = (docId) => {
    // Cookie session is sent automatically (withCredentials); opens the authed download.
    window.open(`${API}/share/${token}/download/${docId}`, "_blank");
  };

  const goLogin = () => navigate(`/login?next=${encodeURIComponent(`/share/${token}`)}`);

  const community = brand?.community || data?.community || "";
  const concierge = brand?.brand || "";
  const accent = brand?.color || "";

  return (
    <div className="min-h-screen bg-sand-50 flex flex-col items-center px-4 py-12" data-testid="shared-documents-page">
      <div className="w-full max-w-2xl">
        <div className="flex items-center gap-2.5 mb-8" data-testid="shared-brand">
          {brand?.logo ? (
            <img src={`${API}/public/tenant/${brand.tenant_id}/logo?use=invite`} alt={community}
                 className="h-10 max-w-[180px] object-contain" />
          ) : (
            <div className="h-9 w-9 rounded-xl flex items-center justify-center" style={{ background: accent || "#e5342e" }}>
              <FileText className="h-5 w-5 text-white" />
            </div>
          )}
          <div className="min-w-0">
            <div className="font-heading font-black text-lg tracking-tight truncate">{community || "Community documents"}</div>
            {concierge && <div className="text-[11px] uppercase tracking-[0.12em] text-sand-400">Shared by {concierge}</div>}
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="h-7 w-7 animate-spin" style={{ color: accent || "#e5342e" }} /></div>
        ) : needsLogin ? (
          <div className="bg-white rounded-3xl border border-sand-200 p-10 text-center flex flex-col items-center gap-3" data-testid="shared-login">
            <div className="h-12 w-12 rounded-2xl flex items-center justify-center" style={{ background: `${accent || "#e5342e"}14` }}><LogIn className="h-6 w-6" style={{ color: accent || "#e5342e" }} /></div>
            <div className="font-heading font-extrabold text-lg">Sign in to view</div>
            <p className="text-sm text-muted-foreground max-w-xs">This document is only available to members of {community || "the community"}. Sign in to open it.</p>
            <button data-testid="shared-login-btn" onClick={goLogin} className="mt-2 inline-flex items-center gap-2 rounded-full bg-ink text-white font-semibold px-5 py-2.5 hover:bg-ink-700 transition-colors"><LogIn className="h-4 w-4" /> Sign in</button>
          </div>
        ) : error ? (
          <div className="bg-white rounded-3xl border border-sand-200 p-10 text-center flex flex-col items-center gap-3" data-testid="shared-error">
            <div className="h-12 w-12 rounded-2xl bg-redline-50 flex items-center justify-center"><AlertCircle className="h-6 w-6 text-redline-500" /></div>
            <div className="font-heading font-extrabold text-lg">Link unavailable</div>
            <p className="text-sm text-muted-foreground max-w-xs">{error}</p>
          </div>
        ) : (
          <div className="bg-white rounded-3xl border border-sand-200 overflow-hidden shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
            <div className="px-6 py-5 border-b border-sand-100">
              <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{data.community}</div>
              <h1 className="font-heading text-2xl font-black tracking-tight flex items-center gap-2 mt-1">
                {data.kind === "folder" ? <Folder className="h-6 w-6" style={{ color: accent || "#e5342e" }} /> : <File className="h-6 w-6" style={{ color: accent || "#e5342e" }} />}
                {data.label}
              </h1>
              <p className="text-sm text-muted-foreground mt-1">{data.files.length} document{data.files.length === 1 ? "" : "s"}</p>
            </div>
            {data.files.length === 0 ? (
              <div className="p-10 text-center text-sm text-muted-foreground">This {data.kind} has no documents yet.</div>
            ) : (
              <div className="divide-y divide-sand-100">
                {data.files.map((f) => (
                  <div key={f.id} data-testid={`shared-file-${f.id}`} className="flex items-center gap-3 px-6 py-4">
                    <span className="h-10 w-10 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${accent || "#e5342e"}14` }}><FileText className="h-5 w-5" style={{ color: accent || "#e5342e" }} strokeWidth={1.5} /></span>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold text-ink truncate">{f.title}</div>
                      <div className="text-xs text-muted-foreground truncate">{f.filename || f.doc_type}</div>
                    </div>
                    {f.has_file && (
                      <button data-testid={`shared-download-${f.id}`} onClick={() => download(f.id)}
                        className="shrink-0 inline-flex items-center gap-1.5 rounded-full bg-ink text-white text-sm font-semibold px-4 py-2 hover:bg-ink-700 transition-colors">
                        <Download className="h-4 w-4" /> Download
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        <p className="text-center text-xs text-muted-foreground mt-6">Members only{community ? ` · ${community}` : ""}</p>
      </div>
    </div>
  );
}
